// Import from libraries
import React, { useState } from 'react';
import PropTypes from 'prop-types';

// Import custom components
import CustomModal from '../../../blocks/custom-modal';
import InfoBox from '../../../blocks/messages/info-box';
import PaymentsService from '../../../../services/PaymentsService';

/**
 * AddNewPaymentModal renders a modal for adding an invoice payment.
 * The user selects the payment mode and enters the payment amount.
 * Text displayed to users remains in French.
 */
const AddNewPaymentModal = ({ open, onClose, onAddPayment, idFolder }) => {
  /** State to store payment mode, amount, error, confirmation state, and payment date */
  const [paymentMode, setPaymentMode] = useState('');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [paymentDate, setPaymentDate] = useState('');

  /**
   * Handle submit of the new payment.
   * - Validate inputs.
   * - Format current date in fr-FR for display; send raw Date (ISO) to backend at confirmation.
   * - Open confirmation modal.
   */
  const handleAddPayment = () => {
    if (!paymentMode || !amount) {
      setError('Veuillez remplir tous les champs.');
      return;
    }

    const parsed = parseFloat(amount);
    if (Number.isNaN(parsed) || parsed < 0) {
      setError('Le montant saisi est invalide.');
      return;
    }

    /** Display date localized; the persisted value will be ISO at confirmation */
    const displayDate = new Date().toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

    setPaymentDate(displayDate);
    setIsConfirmationOpen(true);
  };

  /** Confirm creation and persist to server, then clean up and notify parent. */
  const handleConfirmPayment = async () => {
    try {
      const parsed = parseFloat(amount);

      /** Persist with ISO date (server-friendly). */
      const isoNow = new Date().toISOString();

      const response = await PaymentsService.addFolderPayment({
        id_folder: idFolder,
        payment_mode: paymentMode,
        payment_date: isoNow,
        amount: parsed,
      });

      if (!response) {
        setError("Une erreur est survenue lors de l'ajout du paiement.");
        return;
      }

      /** Notify parent and reset UI */
      onAddPayment?.();
      setPaymentMode('');
      setAmount('');
      setError(null);
      setIsConfirmationOpen(false);
      onClose?.();
    } catch (err) {
      /** FIX: use the actual error from catch block */
      setError(err?.message || 'Une erreur inattendue est survenue');
    }
  };

  /** Cancel confirmation step only */
  const handleCancelConfirmation = () => {
    setIsConfirmationOpen(false);
  };

  return (
    <React.Fragment>
      {/* Main Add Invoice Payment Modal */}
      <CustomModal
        open={open}
        onClose={onClose}
        onCloseLabel="Annuler"
        title="Ajouter un règlement"
        handleSave={handleAddPayment}
        handleSaveLabel="Valider"
      >
        <div className="add-payment-modal-content">
          {error && <InfoBox text={error} type="error" />}

          {/* Select for payment mode */}
          <div className="payment--input">
            <label htmlFor="payment-mode">Mode de paiement</label>
            <select
              id="payment-mode"
              value={paymentMode}
              onChange={(e) => setPaymentMode(e.target.value)}
            >
              <option value="" disabled>
                -- Choisir un mode de paiement --
              </option>
              <option value="cheque">Chèque</option>
              <option value="espece">Espèce</option>
              <option value="cb">CB</option>
              <option value="virement">Virement bancaire</option>
              <option value="paiement_a_distance">Paiement à distance</option>
            </select>
          </div>

          {/* Input for payment amount */}
          <div className="payment--input">
            <label htmlFor="amount">Montant (€)</label>
            <input
              id="amount"
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Saisir le montant"
            />
          </div>
        </div>
      </CustomModal>

      {/* Confirmation Modal */}
      {isConfirmationOpen && (
        <CustomModal
          open={isConfirmationOpen}
          onClose={handleCancelConfirmation}
          onCloseLabel="Annuler"
          title="Confirmer le paiement"
          handleSave={handleConfirmPayment}
          handleSaveLabel="Confirmer"
        >
          <div className="confirmation-modal-content">
            <InfoBox
              text={
                <React.Fragment>
                  <p className="w-100">Vous êtes sur le point d'ajouter le paiement suivant :</p>
                  <ul className="w-100">
                    <li>
                      <strong>Mode de paiement :</strong> {paymentMode}
                    </li>
                    <li>
                      <strong>Montant :</strong> {amount} €
                    </li>
                    <li>
                      <strong>Date :</strong> {paymentDate}
                    </li>
                  </ul>
                  <strong>Cette action ne pourra pas être annulée. Confirmez-vous ?</strong>
                </React.Fragment>
              }
              type="warning"
              disabledCloseBtn={true}
            />
          </div>
        </CustomModal>
      )}
    </React.Fragment>
  );
};

AddNewPaymentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAddPayment: PropTypes.func.isRequired,
  idFolder: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default AddNewPaymentModal;
